import { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { Form } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { computeEffectiveFilters } from '@components/propertyFilters/computeEffectiveFilter';
import {
	buildFilterPayload,
	isFilterActive,
	selectedRange,
} from '@components/propertyFilters/filterUtils';
import { useToast } from '@contexts/Toast.context';
import { useCollectionStore } from '@stores/useCollectionStore';
import { COLLECTION_CONFIG_SCOPE, invalidateCollectionConfig } from '@utils/collectionQueries';
import { saveDetectionObjectPatches } from '../utils/detectionObjectsPayload';

// ─── Helper functions (pure, no hooks) ──────────────────────────────────────────

/**
 * Builds Form initialValues for one object: one field per filter, keyed by
 * filter id → { min, max }.
 */
const buildFormInitialValues = effectiveFilterConfigs => {
	const values = {};
	for (const c of effectiveFilterConfigs) {
		values[c.id] = selectedRange(c);
	}
	return values;
};

/** Compares two { min, max } objects by value. */
const rangesDiffer = (a, b) => {
	if (!a || !b) return a !== b;
	return a.min !== b.min || a.max !== b.max;
};

// ─── Hook ───────────────────────────────────────────────────────────────────────

/**
 * Connects per-object data to an antd Form for that object's filters.
 *
 * Architecture:
 * - Same two-layer model as useCollectionFilters (Form store = draft,
 *   initialValuesRef = saved baseline, currentValues = React-visible mirror).
 * - Object bounds are intersected with collection-level filters via
 *   computeEffectiveFilters — the collection filter acts as a ceiling.
 * - `allObjects` is the collection objects array from detection results —
 *   required so a save preserves the stored entry of every other object.
 *
 * Color used to live in this Form too, which is why `isDirty` and
 * `isFilterDirty` were once two flags. It is edited inline on the tree row now
 * (see `useObjectStyle`), so every change here is a filter change.
 *
 * @param {object} objectData        - The selected object's data (from tree)
 * @param {Array}  collectionFilters - Collection-level filters (constrains object bounds)
 * @param {Array}  allObjects        - All collection objects from useCollectionData
 * @param {string} collectionUuid
 */
const useObjectFilters = (objectData, collectionFilters, allObjects, collectionUuid) => {
	const queryClient = useQueryClient();
	const { addToast } = useToast();
	const [form] = Form.useForm();
	const objectId = objectData?.key;

	// ─── 1. Derived config & baseline ─────────────────────────────────────────

	// Effective bounds = intersection of object's own range with the collection ceiling.
	// If the collection filter says "confidence: 60–90", this object can't exceed that.
	const effectiveFilterConfigs = useMemo(
		() => computeEffectiveFilters(objectData?.filters, collectionFilters),
		[objectData?.filters, collectionFilters]
	);

	// Saved baseline. `effectiveFilterConfigs` is itself memoized on the object's
	// filter bounds, so this survives the reference change objectData takes on
	// every React Query refetch.
	const initialValues = useMemo(
		() => buildFormInitialValues(effectiveFilterConfigs),
		[effectiveFilterConfigs]
	);

	// Ref: lets callbacks compare against the baseline without being in their deps.
	const initialValuesRef = useRef(initialValues);

	// ─── 2. Draft state (mirror of Form store for React reactivity) ───────────

	const [currentValues, setCurrentValues] = useState(initialValues);
	const [isDirty, setIsDirty] = useState(false);
	const [dirtyFilters, setDirtyFilters] = useState({});

	// ─── 3a. Effective config change re-seeding ──────────────────────────────
	//
	// When the collection filter tightens (or loosens), effectiveFilterConfigs
	// changes for the same object. Re-seed the form to the new clamped values
	// so the inputs and slider reflect the current effective bounds.
	//
	// Only re-seeds when not dirty — an in-progress draft is left untouched
	// (the "Adjusted to collection filter range" clamped warning in FilterCard
	// already signals to the user that their values are out of the new bounds).
	//
	// The fingerprint covers effective bounds + saved selections so it fires on
	// both range changes and saved-value changes, not just object switches.

	const effectiveConfigFingerprint = useMemo(
		() =>
			effectiveFilterConfigs
				.map(c => `${c.id}:${c.min}:${c.max}:${c.selectedMin}:${c.selectedMax}`)
				.join('|'),
		[effectiveFilterConfigs]
	);
	const effectiveConfigFingerprintRef = useRef(effectiveConfigFingerprint);

	useEffect(() => {
		if (effectiveConfigFingerprint === effectiveConfigFingerprintRef.current) return;
		if (isDirty) return;

		effectiveConfigFingerprintRef.current = effectiveConfigFingerprint;

		initialValuesRef.current = initialValues;
		form.setFieldsValue(initialValues);
		setCurrentValues(initialValues);
		setDirtyFilters({});
	}, [effectiveConfigFingerprint, initialValues, isDirty, form]);

	// ─── 3b. Object switching ─────────────────────────────────────────────────
	//
	// When the user selects a different object in the tree, reset the form to
	// the new object's baseline. The guard (`prevObjectIdRef`) prevents this
	// from firing when `initialValues` recomputes for the SAME object (e.g.,
	// after a React Query refetch that doesn't actually change values).

	const prevObjectIdRef = useRef(objectId);

	useEffect(() => {
		if (prevObjectIdRef.current === objectId) return;
		prevObjectIdRef.current = objectId;
		initialValuesRef.current = initialValues;
		form.setFieldsValue(initialValues);
		setCurrentValues(initialValues);
		setDirtyFilters({});
		setIsDirty(false);
	}, [objectId, initialValues, form]);

	// ─── 4. Change tracking (Form → React state) ─────────────────────────────
	//
	// Mirrors form values into React state and computes the per-field and
	// overall dirty flags.

	const handleValuesChange = useCallback(
		(_, allValues) => {
			setCurrentValues(allValues);

			const saved = initialValuesRef.current;
			const dirty = {};

			for (const config of effectiveFilterConfigs) {
				dirty[config.id] = rangesDiffer(allValues[config.id], saved[config.id]);
			}

			setDirtyFilters(dirty);
			setIsDirty(Object.values(dirty).some(Boolean));
		},
		[effectiveFilterConfigs]
	);

	// ─── 5. Derived display state ────────────────────────────────────────────

	const activeFilters = useMemo(() => {
		const active = {};
		for (const config of effectiveFilterConfigs) {
			active[config.id] = isFilterActive(currentValues[config.id], config);
		}
		return active;
	}, [currentValues, effectiveFilterConfigs]);

	const activeCount = useMemo(
		() => Object.values(activeFilters).filter(Boolean).length,
		[activeFilters]
	);

	// Shown count — read from the pre-computed per-object map (O(1)) so it doesn't
	// re-scan the whole filtered array on every store update.
	const shownCount = useCollectionStore(s => s.filteredCountByKey[objectId] ?? 0);
	const totalCount = objectData?.count ?? 0;

	// ─── 6. Persistence (mutation) ───────────────────────────────────────────

	const saveMutation = useMutation({
		// Same scope as every other collection-config write — these must not overlap.
		scope: COLLECTION_CONFIG_SCOPE(collectionUuid),
		mutationFn: ({ objectKey, filters }) =>
			saveDetectionObjectPatches(
				queryClient,
				collectionUuid,
				// Filters only. `applyPatch` shallow-merges, so every field named
				// here is this save's chance to clobber it: `style` holds the
				// colour written inline from the tree row, and `is_active` may
				// already be stored false.
				{ [objectKey]: { filters: filters ?? {} } },
				allObjects
			),
		onSuccess: () => {
			// Filters don't change what the model found.
			invalidateCollectionConfig(queryClient, collectionUuid);
			addToast({
				id: 'object-settings-applied',
				title: 'Object filters applied',
				bg: 'success',
				delay: 2500,
			});
		},
		onError: () => {
			addToast({
				id: 'object-settings-error',
				title: 'Failed to save object settings',
				bg: 'danger',
				delay: 4000,
			});
		},
	});
	// `mutate` is referentially stable; the mutation result object is not (safe as a hook dep)
	const { mutate: saveMutate } = saveMutation;

	// ─── 7. User actions ─────────────────────────────────────────────────────

	/** Persist the current filter draft to the backend. */
	const apply = useCallback(() => {
		const snapshot = form.getFieldsValue();
		const targetObjectId = objectId;

		const filterSnapshot = {};
		for (const c of effectiveFilterConfigs) {
			filterSnapshot[c.id] = snapshot[c.id];
		}
		const filterPayload = buildFilterPayload(filterSnapshot, effectiveFilterConfigs);

		saveMutate(
			{ objectKey: objectId, filters: filterPayload },
			{
				onSuccess: () => {
					if (prevObjectIdRef.current !== targetObjectId) return;
					initialValuesRef.current = snapshot;
					setCurrentValues(snapshot);
					setDirtyFilters({});
					setIsDirty(false);
				},
			}
		);
	}, [form, effectiveFilterConfigs, saveMutate, objectId]);

	/** Revert draft to saved baseline. */
	const reset = useCallback(() => {
		const saved = initialValuesRef.current;
		form.setFieldsValue(saved);
		setCurrentValues(saved);
		setDirtyFilters({});
		setIsDirty(false);
	}, [form]);

	/** Reset a single filter to its effective range default. */
	const clearFilter = useCallback(
		filterId => {
			const config = effectiveFilterConfigs.find(c => c.id === filterId);
			if (!config) return;
			form.setFieldValue(filterId, { min: config.min, max: config.max });
			handleValuesChange(null, form.getFieldsValue());
		},
		[form, effectiveFilterConfigs, handleValuesChange]
	);

	// ─── Return ──────────────────────────────────────────────────────────────

	return {
		form,
		initialValues,
		effectiveFilterConfigs,
		currentValues,
		isDirty,
		activeFilters,
		dirtyFilters,
		activeCount,
		shownCount,
		totalCount,
		isSaving: saveMutation.isPending,
		clearFilter,
		apply,
		reset,
		handleValuesChange,
	};
};

export default useObjectFilters;
